import { CalendarDays } from '@tamagui/lucide-icons';
import { ScrollView, ScrollViewBase } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { Group } from '@unistocks/components/group/group';
import { Row } from '@unistocks/components/layout/flex';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { H4, Heading } from '@unistocks/components/text/text';
import { AssetEventListItem } from '@unistocks/features/dashboard/components/dashboard-data/date/components/asset-event';
import { AssetEventDetails } from '@unistocks/features/dashboard/components/dashboard-data/date/components/asset-event-details';
import { usePortfolios } from '@unistocks/features/dashboard/hooks/use-portfolios';
import {
  DashboardEventDto,
  DashboardItemDto,
  EventSubType,
  EventType,
  PortfolioDto,
  SectionAssetDto
} from '@unistocks/proxy/axios-client';
import { useDashboardGetCalendarDataQuery } from '@unistocks/proxy/axios-client/DashboardQuery';
import { formatDate, t } from '@unistocks/shared/localization/index.ts';
import { groupBy, sortByKey } from '@unistocks/shared/utils/util';
import { webCursor } from '@unistocks/shared/utils/web-cursor';
import { Fragment, useState } from 'react';
import { Adapt, getTokenValue, isWeb, Popover, Separator } from 'tamagui';

const PREVIEW_COUNT = 3;

const hasEventDetails = (event: DashboardEventDto) =>
  event.type === EventType.Dividend && event.subType === EventSubType.PaymentDate;
const combinePortfolios: Parameters<typeof usePortfolios<PortfolioDto[]>>[2] = res =>
  res.filter(query => query.isSuccess).map(query => query.data!);

const EventsGroupList: React.FC<{
  groupedEvents: [string, DashboardEventDto[]][];
  assets: SectionAssetDto[];
  portfolios: PortfolioDto[];
}> = ({ groupedEvents, assets, portfolios }) =>
  groupedEvents.map(([date, events]) => (
    <Fragment key={date}>
      <H4 color={'$headingFont'} userSelect="none">
        {formatDate(date, { month: 'long', day: 'numeric' })}
      </H4>
      <Group separator={<Separator />}>
        {events.map((event, index) => {
          const withEventDetails = hasEventDetails(event);

          return (
            <Group.Item key={index}>
              {withEventDetails ? (
                <AssetEventDetails event={event} portfolios={portfolios}>
                  <AssetEventListItem
                    event={event}
                    asset={assets.find(asset => asset.id === event.relatedEntities[0].id)}
                  />
                </AssetEventDetails>
              ) : (
                <AssetEventListItem
                  event={event}
                  asset={assets.find(asset => asset.id === event.relatedEntities[0].id)}
                  {...webCursor('default')}
                  hoverStyle={{
                    bg: 'transparent'
                  }}
                  pressStyle={{
                    bg: 'transparent'
                  }}
                />
              )}
            </Group.Item>
          );
        })}
      </Group>
      <Separator my={'$1'} />
    </Fragment>
  ));

EventsGroupList.displayName = 'EventsGroupList';

export type CalendarEventsListProps = {
  item: DashboardItemDto;
};

export const CalendarEventsList: React.FC<CalendarEventsListProps> = ({
  item: { id, settings }
}) => {
  const [open, setOpen] = useState(false);
  const { data, isSuccess } = useDashboardGetCalendarDataQuery(id);
  const portfolios = usePortfolios(settings.portfolios ?? [], undefined, combinePortfolios);
  const assets = portfolios.flatMap(p => p.sections.flatMap(section => section.assets) ?? []);
  const maxHeight = getTokenValue('$20', 'size') * 1.5;

  if (isSuccess && data?.events.length) {
    const groupedEvents = Array.from(groupBy(data.events, 'date')).sort(sortByKey('0'));
    const hasMore = data.events.length > PREVIEW_COUNT;

    const previewGroups = groupedEvents.reduce<{
      groups: [string, DashboardEventDto[]][];
      remaining: number;
    }>(
      ({ groups, remaining }, [date, events]) => {
        if (remaining <= 0) return { groups, remaining };
        const sliced = events.slice(0, remaining);
        return { groups: [...groups, [date, sliced]], remaining: remaining - sliced.length };
      },
      { groups: [], remaining: PREVIEW_COUNT }
    ).groups;

    return (
      <ScrollView
        scrollEnabled={isWeb}
        showsVerticalScrollIndicator={false}
        $platform-web={{ h: '80%' }}
      >
        <EventsGroupList groupedEvents={previewGroups} assets={assets} portfolios={portfolios} />

        {hasMore && (
          <Popover stayInFrame placement="bottom" size={'$5'} open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
              <Button variant={'link'} size="$3" mt="$2">
                {t('::CalendarEvents:ShowMore')}
              </Button>
            </Popover.Trigger>

            <Popover.Content
              elevation={'$1'}
              padding={'$3'}
              borderWidth={1}
              borderColor="$borderColor"
              maxHeight={maxHeight}
              overflow="hidden"
              enterStyle={{ y: -10, opacity: 0 }}
              exitStyle={{ y: -10, opacity: 0 }}
              transition={'quickLessBouncy'}
            >
              <Popover.Arrow borderWidth={1} borderColor={'$borderColor'} />
              <Row
                display={'none'}
                w={'100%'}
                pb={'$1'}
                alignItems="center"
                borderBottomWidth={1}
                borderColor={'$borderColor'}
                $md={{ display: 'flex' }}
              >
                <HeaderIcon Icon={CalendarDays} color={'$headingFont'} size={'$1'} />
                <Heading size={'$6'} flex={1}>
                  {t('::CalendarEvents:UpcomingEvents')}
                </Heading>
              </Row>
              {/* Tamagui scrollview issue on web */}
              <ScrollViewBase showsVerticalScrollIndicator={false}>
                <EventsGroupList
                  groupedEvents={groupedEvents}
                  assets={assets}
                  portfolios={portfolios}
                />
              </ScrollViewBase>
            </Popover.Content>

            <Adapt when={'max-sm'}>
              <Sheet
                index={1}
                headerIcon={<HeaderIcon Icon={CalendarDays} color={'$headingFont'} />}
                headerTitle={t('::CalendarEvents:UpcomingEvents')}
              >
                <Adapt.Contents />
              </Sheet>
            </Adapt>
          </Popover>
        )}
      </ScrollView>
    );
  }

  return null;
};

CalendarEventsList.displayName = 'CalendarEventsList';
